import React, { Component, ErrorInfo, ReactNode } from 'react';
import { reportError } from '@/instrument';
import {
  Page,
  Card,
  Text,
  Button,
  BlockStack,
  InlineStack,
  Box,
  Icon,
  Banner,
} from '@/lib/polaris';
import { AlertTriangleIcon, RefreshIcon, HomeIcon } from '@/lib/polaris-icons';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary básico para capturar errores de React
 * Previene que un error en un componente rompa toda la aplicación
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    console.error('❌ [ErrorBoundary] getDerivedStateFromError llamado:', error);
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('❌ [ErrorBoundary] Error capturado:', error);
    console.error('❌ [ErrorBoundary] Component Stack:', errorInfo.componentStack);

    this.setState({
      error,
      errorInfo,
    });

    reportError(error, {
      componentStack: errorInfo.componentStack ?? undefined,
      extra: { errorBoundary: true },
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Si hay un fallback personalizado, usarlo
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // UI por defecto de error
      return (
        <Page>
          <Box padding="800">
            <InlineStack align="center">
              <Card>
                <BlockStack gap="500">
                  <InlineStack gap="300" align="center">
                    <Icon source={AlertTriangleIcon} tone="critical" />
                    <BlockStack gap="050">
                      <Text variant="headingLg" fontWeight="semibold" as="h1">
                        Oops! Algo salió mal
                      </Text>
                      <Text variant="bodyMd" tone="subdued" as="p">
                        Se produjo un error inesperado en la aplicación
                      </Text>
                    </BlockStack>
                  </InlineStack>

                  {import.meta.env.DEV && this.state.error && (
                    <Banner tone="critical">
                      <BlockStack gap="200">
                        <Text variant="bodyMd" fontWeight="semibold" as="p">
                          {this.state.error.name}: {this.state.error.message}
                        </Text>
                        {this.state.errorInfo && (
                          <details>
                            <summary style={{ cursor: 'pointer', marginBottom: '8px' }}>
                              <Text variant="bodySm" tone="subdued" as="span">
                                Detalles técnicos (solo en desarrollo)
                              </Text>
                            </summary>
                            <Box padding="200" background="bg-surface-secondary">
                              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                <Text as="span" variant="bodySm">
                                  {this.state.errorInfo.componentStack}
                                </Text>
                              </pre>
                            </Box>
                          </details>
                        )}
                      </BlockStack>
                    </Banner>
                  )}

                  <InlineStack gap="200">
                    <Button onClick={this.handleReset} variant="primary" icon={RefreshIcon}>
                      Intentar de nuevo
                    </Button>
                    <Button onClick={this.handleGoHome} variant="secondary" icon={HomeIcon}>
                      Ir al inicio
                    </Button>
                  </InlineStack>
                </BlockStack>
              </Card>
            </InlineStack>
          </Box>
        </Page>
      );
    }

    return this.props.children;
  }
}
